import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Clubhouse_LeagueType } from "../../enums";
import { useDeleteDraftLeagueMutation } from "../../api/endpoints/leagues";
import { useGetLeagueRosterQuery } from "../../api/endpoints/roster";
import { useGetLeagueTeamsQuery } from "../../api/endpoints/teams";
import { deleteDraftBody } from "./deleteDraftCopy";

interface DeleteDraftDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Runs after a successful delete, once the window has been asked to close. */
  onDeleted?: () => void;
  leagueId: string;
  facilityId: string;
  name: string;
  leagueType: Clubhouse_LeagueType | undefined;
  /** Both callers already hold the round count, so it is not fetched here. */
  roundCount: number;
}

const FINALIZED_ELSEWHERE =
  "This league was finalized, so it can no longer be deleted.";

/**
 * Confirms deleting a draft league, from the dashboard row or the league page.
 *
 * The roster and team counts are read when the window opens rather than carried
 * in by the caller: the dashboard does not have them, and a count that is
 * fetched now cannot be stale. Delete waits for them so the operator never
 * confirms against a sentence that is still filling in.
 */
export function DeleteDraftDialog({
  open,
  onOpenChange,
  onDeleted,
  leagueId,
  facilityId,
  name,
  leagueType,
  roundCount,
}: DeleteDraftDialogProps) {
  const [deleteDraftLeague] = useDeleteDraftLeagueMutation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [failed, setFailed] = useState(false);
  // Delete is disabled while the request is out, so focus falls to Cancel.
  // A failure puts it back, leaving the retry one key away.
  const deleteRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!failed) return;
    // After the window's own focus pass, so the restore is not overwritten.
    const frame = requestAnimationFrame(() => deleteRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [failed]);

  const hasTeams =
    leagueType === Clubhouse_LeagueType.TEAM ||
    leagueType === Clubhouse_LeagueType.SCRAMBLE;
  const args = { facilityId, leagueId };

  const roster = useGetLeagueRosterQuery(args, { skip: !open });
  const teams = useGetLeagueTeamsQuery(args, { skip: !open || !hasTeams });

  const countsLoading = roster.isLoading || (hasTeams && teams.isLoading);
  // A malformed response is treated as unknown, not as an empty roster.
  const players = Array.isArray(roster.data) ? roster.data.length : undefined;
  const teamCount = Array.isArray(teams.data?.teams)
    ? teams.data.teams.length
    : undefined;

  function handleOpenChange(next: boolean) {
    // The request is already out; closing now would hide its outcome.
    if (isDeleting) return;
    if (!next) setFailed(false);
    onOpenChange(next);
  }

  async function handleDelete() {
    setIsDeleting(true);
    setFailed(false);
    try {
      await deleteDraftLeague(args).unwrap();
      toast.success(`${name} was deleted.`);
      setIsDeleting(false);
      onOpenChange(false);
      onDeleted?.();
    } catch (error) {
      setIsDeleting(false);
      if ((error as { status?: number } | undefined)?.status === 409) {
        // Finalized by someone else. The mutation has already refreshed the
        // cache, so closing is enough — there is nothing left to retry.
        toast.error(FINALIZED_ELSEWHERE);
        onOpenChange(false);
        return;
      }
      setFailed(true);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="max-w-[calc(100%-2rem)] sm:max-w-lg"
        header={
          <DialogHeader>
            <DialogTitle className="line-clamp-2 text-lg leading-snug font-semibold break-words text-ink">
              Delete {name}?
            </DialogTitle>
          </DialogHeader>
        }
        footer={
          <div className="flex flex-col gap-3">
            {failed && (
              <p role="alert" className="text-sm font-medium text-danger">
                The league could not be deleted. Please try again.
              </p>
            )}
            <DialogFooter className="flex-row justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isDeleting}
                className="rounded-lg"
              >
                Cancel
              </Button>
              <Button
                ref={deleteRef}
                type="button"
                onClick={() => void handleDelete()}
                disabled={isDeleting || countsLoading}
                className="min-w-32 rounded-lg bg-danger text-white hover:bg-danger/90 focus-visible:ring-danger/30 active:scale-[0.97]"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="animate-spin" aria-hidden="true" />
                    Deleting…
                  </>
                ) : (
                  "Delete league"
                )}
              </Button>
            </DialogFooter>
          </div>
        }
      >
        <DialogDescription
          render={<div />}
          aria-busy={countsLoading}
          className="space-y-3 text-sm"
        >
          {/* The stakes lead at full strength; the reassurance behind it is
              quieter. The counts settle in place, so a screen reader hears the
              sentence change instead of a silent swap. */}
          <p aria-live="polite" className="text-foreground">
            {deleteDraftBody({
              rounds: roundCount,
              players,
              teams: teamCount,
              hasTeams,
            })}
          </p>
          <p className="text-text-muted">
            Players never saw this league, because it was never finalized.
          </p>
        </DialogDescription>
      </DialogContent>
    </Dialog>
  );
}
